import {
  AccountStatusType,
  ActivePaymentPlan,
  FileRead,
  FpBucketBalance,
  RepresentationStatus,
} from "../../../declarations";
import {AccountResolution, CreditorRecalledReason, RecallStatus} from "../../../enums/creditor_recall";
import {PaymentMethodTypes} from "../../../enums/payments";
import {Bankruptcy} from "./bankruptcy_models";
import {CommentToUpdate} from "./comment_models";

export interface CreditorRecallData {
  recall_reason?: CreditorRecalledReason;
  recall_status?: RecallStatus;
  creditor_recall_status_date?: string;
  account_resolution?: AccountResolution;
  buyback_bank_transaction_id?: string;
}

type ItemizationDateType =
  | "last_statement_date"
  | "charge_off_date"
  | "last_payment_date"
  | "transaction_date"
  | "judgement_date"
  | "origination_date";

export type AccountBase = {
  public_id: string;
  id: string;
  product: string;
  status: AccountStatusType;
  batch: string;
  partner_id: number;
  user_id?: number;
  bt_merchant_id?: number;
  nuvei_merchant_id?: number;
  active_payment_plan?: ActivePaymentPlan;

  can_offer_payment_plans?: boolean;
  current_payment_plan_balance_to_collect?: number;
  origination_date: string;
  origination_balance_amount?: number;
  external_account_id: string;
  extra_info?: Record<string, any>;
  freeze_communication: boolean;
  cease_and_desist: boolean;
  last_payment_date?: string;
  last_payment_amount?: number;
  total_balance: number;
  total_paid_on_account: number;
  partner_code?: string;
  partner_name?: string;
  partner_type?: string;
  partner_symbol_url?: string;
  partner_logo_url?: string;
  total_balance_on_charge_off?: number;
  total_current_balance?: number;
  latest_payment_plan_canceled: boolean;
  pending_amount: number;
  is_apple_pay_eligible: boolean;
  is_first_party: boolean;
  nuvei_partner_mid?: string;
  ach_disabled_for_discounted_one_time?: boolean;
  block_credit_cards?: boolean;
  balance_breakdown?: FpBucketBalance[];
  charge_off_type?: ChargeOffType;
  has_voice_ai_app_consent?: boolean;
  collects_delinquent_amounts?: boolean;
  total_delinquent_balance?: number;
  vertical?: Vertical | null;
  sub_product?: SubProduct | null;
  card_last_4_digits?: string | null;
};

export type Account = {
  creditor_recalled: boolean;
  principal_at_placement?: number;
  interest_at_placement?: number;
  fees_at_placement?: number;
  current_principal: number;
  current_interest?: number;
  current_fees?: number;
  account_open_amount?: number;
  itemization_date_type: ItemizationDateType;
  itemization_date: string;
  payment_plan_offers_id?: object;
  initial_transaction_date?: number;
  representation_status: RepresentationStatus;
  first_delinquency_date: string;
  campaign_id?: string;
  active_dispute: boolean;
  active_verification_request: boolean;
  charge_off_date?: string;
  charge_off_fees?: number;
  charge_off_interest?: number;
  charge_off_principal?: number;
  in_pre_legal?: boolean;
  is_legal_eligible?: boolean;
  file_ids: Array<string>;
  original_creditor: string;
  active_complaint: boolean;
  lawsuit: boolean;
  created_at: string;
  primary_account: boolean;
  closed: boolean;
  fraud_reported?: boolean;
  total_paid_on_account_since_itemization_date: number;
  total_fees_accrued_since_itemization_date: number;
  total_interest_accrued_since_itemization_date: number;
  total_balance_on_itemization_date: number;
  current_creditor: string;
  partner_marketing_name?: string;
  credit_report_details: CreditReportDetails;
  should_validate?: boolean;
  in_validation_period?: boolean;
  files?: FileRead[];
  comments: CommentToUpdate[];
  external_collector?: string;
  statute_barred_date?: string;
  statute_barred?: boolean;
  bankruptcy?: boolean;
  coerced_debt?: boolean;
  assignment_date?: string;
  total_balance_at_placement: number;
  bankruptcy_details: Bankruptcy;
  bankruptcy_id?: number;
  num_of_closed_disputes: number;
  approved_military_dispute?: boolean;
  scheduled_for_reporting?: boolean;
  account_status_data?: {
    metadata?: CreditorRecallData;
  };
  legal_collections_read?: LegalCollection[];
  latest_dpd?: number;
  days_until_next_bucket: number;
  total_delinquent_fees?: number;
  total_delinquent_interest?: number;
  total_delinquent_principal?: number;
  bundle_balance_breakdown?: BundleBalanceBreakdown;
  bundle_details?: BundleDetails;
  lowest_monthly_installment_size?: number;
  lowest_hardship_installment_size?: number;
} & AccountBase;

export type AccountFp = {
  email?: string;
  total_delinquent_fees: number;
  total_delinquent_interest: number;
  total_delinquent_principal: number;
  latest_dpd: number;
  returned: boolean;
  bankruptcy: boolean;
  dispute: boolean;
  representation: boolean;
  total_outstanding_balance_not_in_payment_plan: number;
  comments: CommentToUpdate[];
  days_until_next_bucket: number;
  bundle_balance_breakdown?: BundleBalanceBreakdown;
  bundle_details?: BundleDetails;
  representation_status: RepresentationStatus;
} & AccountBase;

type DisputeStatus = "in_dispute" | "dispute_completed" | "not_in_dispute";

type ReportData = {
  last_payment_date_since_last_report?: string;
  sum_of_successful_payment_since_last_report: number;
  balance: number;
};

export type CreditReportDetails = {
  last_report_type: CreditReportingType;
  last_report_date?: string;
  next_report_date?: string;
  first_report_date?: string;
  dispute_status?: DisputeStatus;
  report_data?: ReportData;
  stop_reporting_reason?: string[];
};

export type SelectablePaymentMethod = Extract<PaymentMethodTypes, "card" | "ach" | "apple_pay">;

export interface PaymentMethodSelection {
  methods: SelectablePaymentMethod[];
  default: SelectablePaymentMethod;
}

export interface AccountExternal extends Account {
  payment_method_selection?: PaymentMethodSelection;
}

export enum ChargeOffType {
  PRE_CHARGE_OFF = "PRE_CHARGE_OFF",
  POST_CHARGE_OFF = "POST_CHARGE_OFF",
}

export enum Vertical {
  BNPL = "bnpl",
  CREDIT_CARD = "credit_card",
  POS = "pos",
}

export enum SubProductMarketingType {
  GENERAL = "general",
  AUTO = "auto",
  HOME = "home",
  HEALTHCARE = "healthcare",
  PETS = "pets",
  ELECTRONICS = "electronics",
  JEWELRY = "jewelry",
  FASHION = "fashion",
  BEAUTY = "beauty",
  MUSIC = "music",
}

export type SubProduct = {
  marketing_name?: string | null;
  display_marketing_name?: string | null;
  image_s3_key?: string | null;
  marketing_type?: SubProductMarketingType | null;
};

export interface BundleAccountBreakdown {
  account_id: number;
  product: string;
  sub_product?: string;
  account_public_id: string;
  amount_paid: number;
  original_balance: number;
  original_creditor: string;
  external_account_id: string;
  current_creditor: string;
}

export interface BundleBalanceBreakdown {
  total_bundle_balance: number;
  minimal_installment_size?: number;
  account_breakdowns: BundleAccountBreakdown[];
}

export interface BundleDetails {
  bundle_eligible: boolean;
  bundle_active_payment_plan: boolean;
  bundle_primary_account: boolean;
  bundle_payment_plan_id?: number;
  total_bundle_balance?: number;
  bundle_lowest_monthly_installment_size?: number;
}

export interface AccountSwitcherDetails extends SwitcherDetailsBase {
  charge_off_type: "POST_CHARGE_OFF";
  active_dispute: boolean;
  active_verification_request: boolean;
  current_creditor?: string;
  partner_marketing_name?: string;
  credit_report_details?: CreditReportDetails;
  partner_logo_url: string;
  partner_symbol_url?: string;
  original_creditor: string;
  represented?: boolean;
  scheduled_for_report: boolean;
  should_validate?: boolean;
  total_balance_at_placement: number;
  creditor_recalled?: boolean;
  bankruptcy_id?: number;
  external_collector?: string;
  statute_barred: boolean;
  has_rewards_program?: boolean;
  product?: string;
  bundle_details?: BundleDetails;
  approved_military_dispute?: boolean;
}

export interface BalanceSwitcherDetails extends SwitcherDetailsBase {
  charge_off_type: ChargeOffType.PRE_CHARGE_OFF;
  latest_dpd: number;
  is_balance_out_of_plan_while_account_in_plan: boolean;
  partner_marketing_name?: string;
  partner_logo_url: string;
  partner_symbol_url?: string;
  represented?: boolean;
  active_dispute: boolean;
  active_verification_request: boolean;
  creditor_recalled?: boolean;
  approved_military_dispute?: boolean;
  product?: string | null;
}

export type AccountSwitcherItem = AccountSwitcherDetails | BalanceSwitcherDetails;

export interface SwitcherDetailsBase {
  account_id: string;
  active_payment_plan?: ActivePaymentPlan;
  external_account_id: string;
  partner_code: string;
  lowest_monthly_installment_size?: number;
  lowest_hardship_installment_size?: number;
  one_time_installment_size?: number;
  origination_date?: string;
  public_id?: string;
  status: AccountStatusType;
  total_balance: number;
  closed: boolean;
  is_first_party: boolean;
  primary_account: boolean;
  total_delinquent_balance?: number;
  collects_delinquent_amounts?: boolean;
  vertical?: Vertical | null;
  sub_product?: SubProduct | null;
  card_last_4_digits?: string | null;
}

type CreditReportingType = "not_reported" | "closed" | "reported" | "deleted";

export type {CreditReportingType};

export interface ValidationNoticeAccountDetails {
  account_id: string;
  external_account_id: string;
  partner_code: string;
  partner_name: string;
  original_creditor: string;
  total_balance: number;
  charge_off_date?: string;
  public_id: string;
  itemization_date?: string;
  total_balance_on_itemization_date?: number;
  total_interest_accrued_since_itemization_date?: number;
  total_fees_accrued_since_itemization_date?: number;
  total_paid_on_account_since_itemization_date?: number;
}

export type LegalCollection = {
  vendor: string;
  placement_date?: string;
  claim_id?: string;
  agency_code?: string;
  agency_name?: string;
  agency_phone_number?: string;
  agency_forward_date?: string;
  total_court_costs_recoverable?: number;
  total_cost_balance_recoverable?: number;
  total_court_costs_non_recoverable?: number;
  total_cost_balance_non_recoverable?: number;
  interest_posted?: number;
  payments?: number;
  closed_code?: string;
  closed_date?: string;
  last_payment_date_post_vendor_placement?: string;
  last_payment_amount_post_vendor_placement?: number;
  judgment_date?: string;
  service_date?: string;
  suit_date?: string;
  lien_date?: string;
  garnishment_date?: string;
  judge_docket?: string;
  suit_docket?: string;
  defendant_attorney_details?: {
    name?: string;
    firm?: string;
    address_1?: string;
    address_2?: string;
    city?: string;
    street?: string;
    zipcode?: string;
    phone?: string;
    phone_2?: string;
  };
  address?: {
    street_line_1?: string;
    street_line_2?: string;
    city?: string;
    state?: string;
    zipcode?: string;
  };
};
